import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { setLastRead } from '../../../api/user_conversations';
import { sendMessage } from '../../../api/messages';
import { createActivity, trackPhoneCallClick } from '../../../api/activities';
import { pageInspect } from '../../../api/metainspector';
import { isObjEmpty } from '../../../helpers';
import { isFeatureEnabled, isPhoneCallsEnabled, FEATURES } from '../../../helpers/feature_checker.js';
import { mentorDeclinesCalls } from '../../../helpers/mentor_calls';
import { find as linkifyFind } from 'linkifyjs';

import ComplianceAcknowledgmentModal from './compliance_acknowledgment_modal/index_gated.jsx';
import PostConversationSurveyBanner from '../../questionnaire/post_conversation_survey_banner';
import MessageTriggerNote from './message_trigger_note';
import TranslationToggle, { getLanguageName, isSupportedTranslationLanguage } from './translation_toggle';
import PhoneCall from '../../phone_calls/phone_call';
import { displayCallIncentive } from '../../phone_calls/incentive_banner';
import FeatureCalloutOverlay from '../../overlays/feature_callout_overlay';
import RequestCallModal from './request_call_modal';
import ScheduleCallModal from './schedule_call_modal';
import { isWithinCallHours, formatCallHoursRangeInViewerTimezone } from '../../../helpers/timezone_utils';
import ConnectedCallEventCard from './call_event_card/connected';
import CallSuggestionCard from './call_suggestion_card';
import Context from '../../../utils/context';
import TextareaAutosize from 'react-textarea-autosize';
import { useDebouncedCallback } from 'use-debounce';
import UrlPreview from '../../ui_kit/utilities/url_preview';
import LoadingScreen from '../../system/loading_screen';
import DOMPurify from '../../../utils/purify.min.js';
import S3UploadFile from '../../ui_kit/utilities/s3_upload_file';
import ImageGridDisplay from '../../ui_kit/utilities/image_grid_display';
import linkifyHtml from 'linkify-html';

import { SendMessageButton } from './send_message_button';
import styles from './new_conversation_room.module.css';

const formatMessageTime = (value) =>
  new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

// Build a unified stream of messages, call events, and mentor call
// suggestions sorted by created_at so the cards appear inline at the moment
// they happened. Messages keep a reference to the previous message (not the
// previous stream item) so date breaks and unread indicators behave the same
// as before.
const buildConversationStream = (conversation) => {
  const messages = conversation.messages || [];
  const calls = conversation.calls || [];
  const callSuggestions = conversation.call_suggestions || [];

  const messageItems = messages.map((message, index) => ({
    kind: 'message',
    key: `msg-${message.id || index}`,
    sortKey: new Date(message.created_at).getTime(),
    secondaryKey: 0,
    message,
    index,
    previousMessage: index > 0 ? messages[index - 1] : null,
  }));

  const callItems = calls.map((call) => ({
    kind: 'call',
    key: `call-${call.uuid}`,
    sortKey: new Date(call.created_at).getTime(),
    secondaryKey: 1,
    call,
  }));

  const suggestionItems = callSuggestions.map((suggestion) => ({
    kind: 'call_suggestion',
    key: `call-suggestion-${suggestion.uuid}`,
    sortKey: new Date(suggestion.created_at).getTime(),
    secondaryKey: 1,
    suggestion,
  }));

  return [...messageItems, ...callItems, ...suggestionItems].sort(
    (a, b) => a.sortKey - b.sortKey || a.secondaryKey - b.secondaryKey
  );
};

const CALL_MODAL_REQUEST = 'request';
const CALL_MODAL_SCHEDULE = 'schedule';

export default function Conversation({
  conversation,
  user,
  backPath,
  profilePath,
  onProfileOpen,
  fetchConversation,
  otherUser,
  sendFunction = () => {},
  showCallsCallout = false,
  onDismissCallsCallout,
  showSurveyBanner = false,
  surveyMentorName,
  onStartSurvey,
}) {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [currentMessage, setCurrentMessage] = useState(searchParams.get('firstMessage') || '');
  const lastReadTime = conversation.current_user_conversation.last_read_time;
  const [heldKey, setHeldKey] = useState(null);
  const context = useContext(Context);
  const viewport = context.viewport;
  const currentAccount = context.auth.currentAccount();
  const currentUser = context.auth.getCurrentUser();
  const isPhoneFeatureEnabled = isPhoneCallsEnabled(currentAccount);
  const isPhoneCallsOnly = isFeatureEnabled(currentAccount, FEATURES.PHONE_CALLS_ONLY);
  const isTranslationFeatureEnabled = isFeatureEnabled(currentAccount, FEATURES.TRANSLATIONS);
  const isTriggerNotesEnabled = isFeatureEnabled(currentAccount, FEATURES.MESSAGE_TRIGGER_NOTES);
  const isAttachmentsDisabled = isFeatureEnabled(currentAccount, FEATURES.DISALLOW_ATTACHMENTS_IN_MESSAGES);
  const otherUserAcceptsCalls = Boolean(otherUser?.profile?.accepts_calls);
  const showPhoneFeature = Boolean(isPhoneFeatureEnabled && otherUser && otherUserAcceptsCalls);
  const showMessageInput = Boolean(!isPhoneCallsOnly);
  const [complianceAcknowledgedAt, setComplianceAcknowledgedAt] = useState(
    conversation.current_user_compliance_acknowledged_at || null
  );

  const showComplianceModal = conversation.compliance_acknowledgment_required && !complianceAcknowledgedAt;
  const isMobile = viewport.getIsMobile();
  const isMentor = user?.current_profile_type === 'PatientPartner';
  const showMentorDeclinesCalls = mentorDeclinesCalls({
    viewerProfileType: user?.current_profile_type,
    phoneFeatureEnabled: isPhoneFeatureEnabled,
    profile: otherUser?.profile,
  });
  const [activeCall, setActiveCall] = useState(conversation.outgoing_call || null);
  const [callModal, setCallModal] = useState(null);
  const [pendingSmsCallRequest, setPendingSmsCallRequest] = useState(false);
  const [urlPreview, setUrlPreview] = useState();
  const [isUrlPreviewLoading, setIsUrlPreviewLoading] = useState(false);
  const [urlMetadata, setUrlMetadata] = useState(null);
  const messageInputRef = useRef(null);
  const mobileActionsMenuRef = useRef(null);
  const [avatarImageFailed, setAvatarImageFailed] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const currentTranslationLanguage = conversation.current_user_conversation?.translate_to_language;
  const [targetLanguage, setTargetLanguage] = useState(
    isSupportedTranslationLanguage(currentTranslationLanguage) ? currentTranslationLanguage : null
  );
  const otherUserImageUrl = otherUser?.profile?.photo || '';
  const showOtherUserImage = otherUserImageUrl && !avatarImageFailed;
  const otherUserName = otherUser?.profile?.display_name || 'Community member';
  const mentorFirstName = otherUser?.profile?.display_name?.trim()?.split(/\s+/)?.[0] || 'your mentor';
  const otherUserTimezone = otherUser?.user?.timezone;
  const callable = isWithinCallHours(otherUserTimezone);
  const callHoursRange = formatCallHoursRangeInViewerTimezone(otherUserTimezone);
  const otherUserFirstName = otherUser?.profile?.display_name?.trim()?.split(/\s+/)?.[0];
  const conversationCalls = conversation.calls || [];
  const conversationCallSuggestions = conversation.call_suggestions || [];
  const latestCallSuggestionUuid =
    conversationCallSuggestions.length > 0
      ? conversationCallSuggestions[conversationCallSuggestions.length - 1].uuid
      : null;
  const hasActiveConversationCall = conversationCalls.some((call) =>
    ['pending', 'accepted', 'in_progress'].includes(call.status)
  );
  const callIncentive = displayCallIncentive(currentAccount, conversation);
  const closeCallModal = () => setCallModal(null);
  const handleCalloutCallCreated = () => {
    closeCallModal();
    fetchConversation(false);
  };
  const openCallNow = () => {
    if (typeof onDismissCallsCallout === 'function') onDismissCallsCallout();
    setCallModal(CALL_MODAL_REQUEST);
  };
  const openScheduleForLater = () => {
    if (typeof onDismissCallsCallout === 'function') onDismissCallsCallout();
    setCallModal(CALL_MODAL_SCHEDULE);
  };
  const handleCalloutRequestCall = () => {
    trackPhoneCallClick('callout_request_a_call', { conversation_id: conversation.uuid });
    openCallNow();
  };
  const handleCalloutScheduleCall = () => {
    trackPhoneCallClick('callout_schedule_a_call', { conversation_id: conversation.uuid });
    openScheduleForLater();
  };
  const handleCalloutDismiss = () => {
    trackPhoneCallClick('callout_dismiss', { conversation_id: conversation.uuid });
    if (typeof onDismissCallsCallout === 'function') onDismissCallsCallout();
  };
  const handleSuggestionCardRequestCall = () => {
    trackPhoneCallClick('call_suggestion_card_request_a_call', { conversation_id: conversation.uuid });
    openCallNow();
  };
  const handleSuggestionCardScheduleCall = () => {
    trackPhoneCallClick('call_suggestion_card_schedule_for_later', { conversation_id: conversation.uuid });
    openScheduleForLater();
  };
  const handleIntroHowCallsWork = () => {
    trackPhoneCallClick('intro_tile_how_calls_work', { conversation_id: conversation.uuid });
  };
  const handleIntroRequestCall = () => {
    trackPhoneCallClick('intro_tile_request_a_call', { conversation_id: conversation.uuid });
    openCallNow();
  };
  const handleIntroScheduleCall = () => {
    trackPhoneCallClick('intro_tile_schedule_a_call', { conversation_id: conversation.uuid });
    openScheduleForLater();
  };
  const howCallsWorkPath = `/member/phone_calls/mentee?return_to=${encodeURIComponent(
    `/member/conversations/${conversation.uuid}`
  )}`;
  const otherUserInitials = otherUserName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const otherUserLastActiveDisplay = otherUser?.profile?.last_active && otherUser?.profile?.last_active_display;
  const otherUserSubtitle = otherUserLastActiveDisplay ? `Active ${otherUserLastActiveDisplay}` : null;
  const headerStatus = (
    <>
      {showMentorDeclinesCalls && <span className={styles.chat_header_no_calls}>Does not accept calls</span>}
      {otherUserSubtitle && <span>{otherUserSubtitle}</span>}
    </>
  );
  const canOpenProfile = isMobile && typeof onProfileOpen === 'function';
  const headerAvatar = showOtherUserImage ? (
    <span className={styles.chat_header_avatar}>
      <img src={otherUserImageUrl} alt='' draggable='false' onError={() => setAvatarImageFailed(true)} />
    </span>
  ) : (
    <span className={styles.chat_header_avatar_initials}>{otherUserInitials || 'PP'}</span>
  );

  useEffect(() => {
    setComplianceAcknowledgedAt(conversation.current_user_compliance_acknowledged_at || null);
  }, [conversation.uuid, conversation.current_user_compliance_acknowledged_at]);

  useEffect(() => {
    const nextLanguage = conversation.current_user_conversation?.translate_to_language;
    setTargetLanguage(isSupportedTranslationLanguage(nextLanguage) ? nextLanguage : null);
    setMobileActionsOpen(false);
  }, [conversation.uuid, conversation.current_user_conversation?.translate_to_language]);

  useEffect(() => {
    if (!mobileActionsOpen) return undefined;

    const closeMobileActions = (event) => {
      if (mobileActionsMenuRef.current && !mobileActionsMenuRef.current.contains(event.target)) {
        setMobileActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeMobileActions);
    return () => document.removeEventListener('mousedown', closeMobileActions);
  }, [mobileActionsOpen]);

  useEffect(() => {
    setAvatarImageFailed(false);
  }, [otherUserImageUrl]);

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile]);

  useEffect(() => {
    setActiveCall(conversation.outgoing_call || null);
  }, [conversation.outgoing_call?.uuid, conversation.outgoing_call?.status, conversation.outgoing_call?.counter_count]);

  // Patients arriving from the "mentor suggested a call" SMS land here with
  // utm_source=SuggestedCallSms on the conversation URL. Strip the param so a
  // refresh or later visit doesn't reopen the modal, but remember the intent
  // in state so the request-call modal can still auto-open after blocking
  // overlays (e.g. the compliance modal) are dismissed.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('utm_source') !== 'SuggestedCallSms') {
      setPendingSmsCallRequest(false);
      return;
    }

    params.delete('utm_source');
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    });

    if (isMentor) return;
    if (!showPhoneFeature || !otherUser?.profile?.accepts_calls) return;

    setPendingSmsCallRequest(true);
  }, [conversation.uuid]);

  useEffect(() => {
    if (!pendingSmsCallRequest) return;
    if (showComplianceModal) return;

    setPendingSmsCallRequest(false);
    if (!hasActiveConversationCall) {
      setCallModal(CALL_MODAL_REQUEST);
    }
  }, [pendingSmsCallRequest, showComplianceModal, hasActiveConversationCall]);

  let defaultFiles = [];
  if (currentMessage.message_images) {
    defaultFiles = currentMessage.message_images.map((i) => i.image);
  }
  const [files, setFiles] = useState(defaultFiles);
  const [fileLabelsByUrl, setFileLabelsByUrl] = useState({});
  const send = (e) => {
    if (e !== undefined) {
      e.preventDefault();
    }

    if (currentMessage.replace(/\n/g, '') === '' && files.length === 0) {
      return;
    }
    setCurrentMessage('');
    onSendClick(currentMessage, urlMetadata);
    setFiles([]);
    setFileLabelsByUrl({});
  };
  const onRead = () => {
    setLastRead(conversation.id, () => fetchConversation());
  };

  const onSendClick = (currentMessage, urlMetadata) => {
    createActivity('Activity::InitiateConnection', { conversation_id: conversation.uuid });
    sendMessage(conversation.uuid, currentMessage, urlMetadata, files, () => {
      setUrlPreview(null);
      setUrlMetadata(null);
      fetchConversation(false, true);
    });
    sendFunction();
  };

  const clickNewMessages = () => {
    const messageBody = document.querySelector('[data-message-scroll]');
    if (messageBody) {
      messageBody.scrollTop = messageBody.scrollHeight - messageBody.clientHeight;
    }
  };

  const openReportAnEvent = () => {
    setMobileActionsOpen(false);
    history.push('/report-an-event');
  };

  const showUrlPreview = (newBody) => {
    const link = linkifyFind(newBody).find((l) => l.type === 'url');
    if (link && link.href !== urlPreview) {
      setUrlPreview(link.href);
      pageInspect(link.href, (data) => {
        if (!isObjEmpty(data) && data.title && data.description && data.image) {
          setUrlMetadata(data);
        }
        setIsUrlPreviewLoading(false);
      });
      setIsUrlPreviewLoading(true);
    }
  };

  const removeUrlPreview = () => {
    setUrlMetadata(null);
    setUrlPreview(null);
  };

  const attachmentLabel = (file) => {
    const savedLabel = typeof file === 'string' ? fileLabelsByUrl[file] : null;
    if (savedLabel) return savedLabel;

    if (typeof file !== 'string') return 'Attachment';

    const filename = decodeURIComponent(file.split('?')[0].split('/').pop() || '');
    if (/^[0-9a-f-]+-\d+\.[a-z0-9]+$/i.test(filename)) return 'Attachment';
    return filename || 'Attachment';
  };

  const removeAttachment = (indexToRemove) => {
    setFiles((currentFiles) => {
      const removedFile = currentFiles[indexToRemove];

      if (removedFile) {
        setFileLabelsByUrl((currentLabels) => {
          if (!currentLabels[removedFile]) return currentLabels;

          const nextLabels = { ...currentLabels };
          delete nextLabels[removedFile];
          return nextLabels;
        });
      }

      return currentFiles.filter((_, index) => index !== indexToRemove);
    });
  };

  const debouncedShowUrlPreview = useDebouncedCallback((value) => {
    showUrlPreview(value);
  }, 1000);

  const onBodyChange = (event) => {
    setCurrentMessage(event.target.value);
    debouncedShowUrlPreview(event.target.value);
  };

  return (
    <div className={styles.chat_room} style={{ flex: 1, minHeight: 0 }}>
      {showComplianceModal && (
        <ComplianceAcknowledgmentModal
          conversationUuid={conversation.uuid}
          accountSlug={currentAccount?.slug}
          role={isMentor ? 'mentor' : 'patient'}
          onAcknowledged={(acknowledgedAt) => {
            setComplianceAcknowledgedAt(acknowledgedAt);
            // Refetch so the parent (which gates the calls-feature callout) sees
            // current_user_compliance_acknowledged_at populated and can stop
            // treating the compliance modal as a blocking overlay.
            fetchConversation(false);
          }}
        />
      )}
      <div className={styles.chat_room_header}>
        <div className={styles.chat_header_identity}>
          {isMobile && backPath && (
            <Link to={backPath} className={styles.chat_header_back} aria-label='Back to conversations'>
              <svg
                width='20'
                height='20'
                viewBox='0 0 256 256'
                fill='currentColor'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
              >
                <path d='M168.49,199.51a12,12,0,0,1-17,17l-80-80a12,12,0,0,1,0-17l80-80a12,12,0,0,1,17,17L97,128Z' />
              </svg>
            </Link>
          )}

          {canOpenProfile ? (
            <button
              type='button'
              className={styles.chat_header_profile_button}
              onClick={onProfileOpen}
              aria-label={`View ${otherUserName} profile`}
            >
              {headerAvatar}
              <span className={styles.chat_header_text}>
                <p>{otherUserName}</p>
                {headerStatus}
              </span>
            </button>
          ) : (
            <>
              {isMobile &&
                (profilePath ? (
                  <Link to={profilePath} className={styles.chat_header_avatar_link}>
                    {headerAvatar}
                  </Link>
                ) : (
                  headerAvatar
                ))}

              <div className={styles.chat_header_text}>
                <p>{otherUserName}</p>
                {headerStatus}
              </div>
            </>
          )}
        </div>

        <div className={styles.chat_header_actions}>
          {showPhoneFeature && (
            <div className={styles.chat_header_call} data-callout-target='phone_feature_mobile'>
              <PhoneCall
                otherUser={otherUser}
                conversationId={conversation.uuid}
                activeCall={activeCall}
                onCallCreated={setActiveCall}
                currentUserTimezone={user?.timezone}
                incentive={callIncentive}
              />
            </div>
          )}
          {isTranslationFeatureEnabled && (
            <TranslationToggle
              conversationUuid={conversation.uuid}
              initialLanguage={conversation.current_user_conversation?.translate_to_language}
              onLanguageChange={(lang) => {
                setTargetLanguage(lang);
                fetchConversation(false);
              }}
            />
          )}
          {currentUser.current_profile_type == 'PatientPartner' &&
            (isMobile ? (
              <div className={styles.chat_header_more_wrap} ref={mobileActionsMenuRef}>
                <button
                  type='button'
                  className={`${styles.chat_header_more} ${mobileActionsOpen ? styles.chat_header_more_open : ''}`}
                  aria-label='Conversation actions'
                  aria-haspopup='menu'
                  aria-expanded={mobileActionsOpen}
                  onClick={() => setMobileActionsOpen((open) => !open)}
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 256 256'
                    fill='currentColor'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path d='M112,60a16,16,0,1,1,16,16A16,16,0,0,1,112,60Zm16,52a16,16,0,1,0,16,16A16,16,0,0,0,128,112Zm0,68a16,16,0,1,0,16,16A16,16,0,0,0,128,180Z' />
                  </svg>
                </button>
                {mobileActionsOpen && (
                  <div className={styles.chat_header_more_menu} role='menu'>
                    <button
                      type='button'
                      className={styles.chat_header_more_item}
                      role='menuitem'
                      onClick={openReportAnEvent}
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 256 256'
                        fill='currentColor'
                        xmlns='http://www.w3.org/2000/svg'
                        aria-hidden='true'
                      >
                        <path d='M42.76,50A8,8,0,0,0,40,56V224a8,8,0,0,0,16,0V179.77c26.79-21.16,49.87-9.75,76.45,3.41,16.4,8.11,34.06,16.85,53,16.85,13.93,0,28.54-4.75,43.82-18a8,8,0,0,0,2.76-6V56A8,8,0,0,0,218.76,50c-28,24.23-51.72,12.49-79.21-1.12C111.07,34.76,78.78,18.79,42.76,50ZM216,172.25c-26.79,21.16-49.87,9.74-76.45-3.41-25-12.35-52.81-26.13-83.55-8.4V59.79c26.79-21.16,49.87-9.75,76.45,3.4,25,12.35,52.82,26.13,83.55,8.4Z' />
                      </svg>
                      <span>Flag</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type='button'
                className={styles.chat_header_report}
                aria-label='Flag this conversation'
                title='Flag this conversation'
                onClick={openReportAnEvent}
              >
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 256 256'
                  fill='currentColor'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden='true'
                >
                  <path d='M42.76,50A8,8,0,0,0,40,56V224a8,8,0,0,0,16,0V179.77c26.79-21.16,49.87-9.75,76.45,3.41,16.4,8.11,34.06,16.85,53,16.85,13.93,0,28.54-4.75,43.82-18a8,8,0,0,0,2.76-6V56A8,8,0,0,0,218.76,50c-28,24.23-51.72,12.49-79.21-1.12C111.07,34.76,78.78,18.79,42.76,50ZM216,172.25c-26.79,21.16-49.87,9.74-76.45-3.41-25-12.35-52.81-26.13-83.55-8.4V59.79c26.79-21.16,49.87-9.75,76.45,3.4,25,12.35,52.82,26.13,83.55,8.4Z' />
                </svg>
              </button>
            ))}
        </div>
      </div>
      {conversation && (
        <div className={styles.message_container_otr} data-message-scroll>
          <div className={styles.message_container}>
            <div className={styles.message_inner}>
              {!isMentor && otherUser && conversation.messages.length == 0 && (
                <div className={styles.intro_tile}>
                  <p className={styles.intro_tile_eyebrow}>PatientPartner</p>
                  <p className={styles.intro_tile_text}>
                    You're matched with {mentorFirstName}! This is a safe space to share what you're going through, ask
                    questions, and hear about someone else's personal experience. Every mentor's journey is different,
                    and they're here to offer peer support based on what they've been through themselves.
                  </p>
                  <p className={styles.intro_tile_text}>
                    {isPhoneCallsOnly ? (
                      <>
                        To help you navigate, please take some time to view{' '}
                        <Link
                          to={howCallsWorkPath}
                          className={styles.intro_tile_link}
                          onClick={handleIntroHowCallsWork}
                        >
                          How calls work
                        </Link>
                        .
                        {showPhoneFeature && (
                          <>
                            {' '}
                            Then, feel free to{' '}
                            <button type='button' className={styles.intro_tile_link} onClick={handleIntroRequestCall}>
                              request
                            </button>{' '}
                            or{' '}
                            <button type='button' className={styles.intro_tile_link} onClick={handleIntroScheduleCall}>
                              schedule a call
                            </button>{' '}
                            with your mentor.
                          </>
                        )}
                      </>
                    ) : (
                      "To help start the conversation, try sharing a little about where you are in your journey or what's been on your mind lately."
                    )}
                  </p>
                  <p className={styles.intro_tile_text}>
                    Mentors are volunteers so please give them some time to answer.
                  </p>
                </div>
              )}
              {conversation &&
                lastReadTime !== null &&
                buildConversationStream(conversation).map((item) => {
                  if (item.kind === 'call') {
                    return (
                      <ConnectedCallEventCard
                        key={item.key}
                        call={item.call}
                        conversationUuid={conversation.uuid}
                        isMentor={isMentor}
                        viewerTimezone={user?.timezone}
                        viewerPhone={user?.phone}
                        otherUserFirstName={otherUser?.profile?.display_name?.split(' ')[0]}
                        otherUserName={otherUser?.profile?.display_name}
                        otherUserPhoto={otherUser?.profile?.photo}
                        otherUserTimezone={otherUser?.user?.timezone}
                        onCallActed={() => fetchConversation(false)}
                        onScheduleCall={openScheduleForLater}
                      />
                    );
                  }

                  if (item.kind === 'call_suggestion') {
                    const suggestionAddressed = conversationCalls.some(
                      (call) => new Date(call.created_at) >= new Date(item.suggestion.created_at)
                    );
                    const suggestionActionable =
                      !isMentor &&
                      item.suggestion.uuid === latestCallSuggestionUuid &&
                      !suggestionAddressed &&
                      !hasActiveConversationCall;

                    return (
                      <CallSuggestionCard
                        key={item.key}
                        isMentor={isMentor}
                        mentorFirstName={isMentor ? undefined : otherUserFirstName}
                        patientFirstName={isMentor ? otherUserFirstName : undefined}
                        actionable={suggestionActionable}
                        onCallNow={handleSuggestionCardRequestCall}
                        onSchedule={handleSuggestionCardScheduleCall}
                      />
                    );
                  }

                  const { message, index, previousMessage } = item;
                  const currentMessageCurrentUser = message.current_user_sent;
                  const currentMessageMinute = Math.floor(new Date(message.created_at).getTime() / 60000);
                  const previousMessageMinute = previousMessage
                    ? Math.floor(new Date(previousMessage.created_at).getTime() / 60000)
                    : null;
                  const shouldShowTimestamp =
                    !previousMessage ||
                    message.current_user_sent !== previousMessage.current_user_sent ||
                    currentMessageMinute !== previousMessageMinute;
                  const messageGroupSpacingClass =
                    previousMessage && message.current_user_sent !== previousMessage.current_user_sent
                      ? styles.message_bubble_spaced
                      : '';
                  return (
                    <React.Fragment key={item.key}>
                      {(!previousMessage ||
                        new Date(message.created_at).getDate() !== new Date(previousMessage.created_at).getDate()) && (
                        <p className={`text-center ${styles.message_date}`}>{`${new Date(
                          message.created_at
                        ).toLocaleString('default', { month: 'short' })} ${new Date(message.created_at).getDate()}`}</p>
                      )}
                      {index === 0 &&
                        !message.current_user_sent &&
                        new Date(message.created_at).getTime() > new Date(lastReadTime).getTime() && (
                          <div
                            onClick={() => {
                              clickNewMessages();
                              onRead();
                            }}
                            className={styles.new_message_txt_wrap}
                          >
                            <div className={styles.new_message_txt}>
                              <h6>New Messages</h6>
                              <svg
                                width='15'
                                height='8'
                                viewBox='0 0 15 8'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M1.00177 1.84135e-05C1.23542 -0.000438079 1.46185 0.0809368 1.64177 0.230018L7.00177 4.71002L12.3718 0.39002C12.4741 0.306954 12.5918 0.244922 12.7181 0.20749C12.8444 0.170059 12.9769 0.157965 13.1079 0.171904C13.239 0.185843 13.366 0.22554 13.4816 0.288714C13.5972 0.351888 13.6992 0.437293 13.7818 0.540019C13.8648 0.642307 13.9269 0.760003 13.9643 0.886343C14.0017 1.01268 14.0138 1.14517 13.9999 1.2762C13.9859 1.40723 13.9462 1.53421 13.8831 1.64985C13.8199 1.76549 13.7345 1.8675 13.6318 1.95002L7.63177 6.78002C7.45283 6.9271 7.22839 7.00751 6.99677 7.00751C6.76514 7.00751 6.5407 6.9271 6.36177 6.78002L0.361767 1.78002C0.260509 1.69607 0.176806 1.59297 0.115454 1.47662C0.0541022 1.36027 0.016305 1.23297 0.00422911 1.10199C-0.00784675 0.971013 0.00603624 0.838941 0.0450807 0.713337C0.0841252 0.587734 0.147564 0.471067 0.231766 0.370018C0.32482 0.255305 0.442132 0.162631 0.575268 0.098656C0.708405 0.0346812 0.85406 0.000995185 1.00177 1.84135e-05Z'
                                  fill='white'
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      {previousMessage &&
                        !message.current_user_sent &&
                        new Date(message.created_at).getTime() > new Date(lastReadTime).getTime() &&
                        new Date(previousMessage.created_at).getTime() < new Date(lastReadTime).getTime() && (
                          <div
                            onClick={() => {
                              clickNewMessages();
                              onRead();
                            }}
                            className={styles.new_message_txt_wrap}
                          >
                            <div className={styles.new_message_txt}>
                              <h6>New Messages</h6>
                              <svg
                                width='15'
                                height='8'
                                viewBox='0 0 15 8'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M1.00177 1.84135e-05C1.23542 -0.000438079 1.46185 0.0809368 1.64177 0.230018L7.00177 4.71002L12.3718 0.39002C12.4741 0.306954 12.5918 0.244922 12.7181 0.20749C12.8444 0.170059 12.9769 0.157965 13.1079 0.171904C13.239 0.185843 13.366 0.22554 13.4816 0.288714C13.5972 0.351888 13.6992 0.437293 13.7818 0.540019C13.8648 0.642307 13.9269 0.760003 13.9643 0.886343C14.0017 1.01268 14.0138 1.14517 13.9999 1.2762C13.9859 1.40723 13.9462 1.53421 13.8831 1.64985C13.8199 1.76549 13.7345 1.8675 13.6318 1.95002L7.63177 6.78002C7.45283 6.9271 7.22839 7.00751 6.99677 7.00751C6.76514 7.00751 6.5407 6.9271 6.36177 6.78002L0.361767 1.78002C0.260509 1.69607 0.176806 1.59297 0.115454 1.47662C0.0541022 1.36027 0.016305 1.23297 0.00422911 1.10199C-0.00784675 0.971013 0.00603624 0.838941 0.0450807 0.713337C0.0841252 0.587734 0.147564 0.471067 0.231766 0.370018C0.32482 0.255305 0.442132 0.162631 0.575268 0.098656C0.708405 0.0346812 0.85406 0.000995185 1.00177 1.84135e-05Z'
                                  fill='white'
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      {currentMessageCurrentUser && (
                        <>
                          <div
                            className={`${styles.message_group} ${styles.message_group_current_user} ${messageGroupSpacingClass}`}
                          >
                            <div className={`${styles.message_bubble} ${styles.current_user}`}>
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    linkifyHtml(message.body.replaceAll('\n', '<br/>'), { target: 'blank' })
                                  ),
                                }}
                              ></p>
                              {message.url_preview_metadata && <UrlPreview metadata={message.url_preview_metadata} />}
                              {message.message_images.length > 0 && (
                                <div className={styles.message_image_grid}>
                                  <ImageGridDisplay photos={message.message_images.map((i) => i.image)} />
                                </div>
                              )}
                            </div>
                            {shouldShowTimestamp && (
                              <p className={styles.message_timestamp}>{formatMessageTime(message.created_at)}</p>
                            )}
                          </div>
                          {isTriggerNotesEnabled &&
                            message.triggered_notes?.map((note, noteIndex) => (
                              <MessageTriggerNote key={noteIndex} note={note} />
                            ))}
                        </>
                      )}
                      {!currentMessageCurrentUser && (
                        <>
                          <div
                            className={`${styles.message_group} ${styles.message_group_other_user} ${messageGroupSpacingClass}`}
                          >
                            <div className={`${styles.message_bubble} ${styles.other_user} fs-mask`}>
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    linkifyHtml(message.body.replaceAll('\n', '<br/>'), { target: 'blank' })
                                  ),
                                }}
                              ></p>
                              {message.url_preview_metadata && <UrlPreview metadata={message.url_preview_metadata} />}
                              {message.message_images.length > 0 && (
                                <div className={styles.message_image_grid}>
                                  <ImageGridDisplay photos={message.message_images.map((i) => i.image)} />
                                </div>
                              )}
                            </div>
                            {shouldShowTimestamp && (
                              <p className={styles.message_timestamp}>{formatMessageTime(message.created_at)}</p>
                            )}
                          </div>
                          {isTriggerNotesEnabled &&
                            message.triggered_notes?.map((note, noteIndex) => (
                              <MessageTriggerNote key={noteIndex} note={note} />
                            ))}
                          {isTranslationFeatureEnabled && targetLanguage && message.translations?.[targetLanguage] && (
                            <div className={`${styles.translated_box} fs-mask`}>
                              <div className={styles.translated_box_label}>
                                {getLanguageName(targetLanguage)} Translation
                              </div>
                              {message.translations[targetLanguage]}
                            </div>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        </div>
      )}
      {conversation && (
        <div>
          <div ref={messageInputRef} className={styles.message_otr}>
            {showCallsCallout && otherUserAcceptsCalls && (
              <FeatureCalloutOverlay
                body={
                  <>
                    Talk with <strong>{mentorFirstName}</strong> by phone — free, private, and faster than typing it all
                    out.
                  </>
                }
                incentive={callIncentive}
                mentorName={mentorFirstName}
                onPrimary={handleCalloutRequestCall}
                onSecondary={handleCalloutScheduleCall}
                onDismiss={handleCalloutDismiss}
                closeAriaLabel='Dismiss call invitation'
              />
            )}
            {showSurveyBanner && (
              <PostConversationSurveyBanner mentorName={surveyMentorName || mentorFirstName} onStart={onStartSurvey} />
            )}
            <div className={styles.message_attachment_preview_row} style={{ height: files.length === 0 ? '0px' : '' }}>
              <div className={styles.message_attachment_preview_col}>
                <div className={styles.message_attachment_chips}>
                  {files.map((file, index) => (
                    <div key={`${file}-${index}`} className={styles.message_attachment_chip}>
                      <span className={styles.message_attachment_chip_icon} aria-hidden='true'>
                        <svg
                          width='13'
                          height='13'
                          viewBox='0 0 256 256'
                          fill='currentColor'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path d='M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216V158.75l-26.34-26.35a8,8,0,0,0-11.32,0L150.63,160.1l-53-53a8,8,0,0,0-11.32,0L40,153.37ZM216,200H40V176l52-52,80,80H216Zm-40-96a16,16,0,1,1,16-16A16,16,0,0,1,176,104Z' />
                        </svg>
                      </span>
                      <span className={styles.message_attachment_chip_label}>{attachmentLabel(file)}</span>
                      <button
                        type='button'
                        className={styles.message_attachment_chip_remove}
                        onClick={() => removeAttachment(index)}
                        aria-label='Remove attachment'
                      >
                        <svg
                          width='11'
                          height='11'
                          viewBox='0 0 256 256'
                          fill='currentColor'
                          xmlns='http://www.w3.org/2000/svg'
                          aria-hidden='true'
                        >
                          <path d='M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z' />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {showMessageInput && (
              <div className={styles.type_message_row}>
                {!isAttachmentsDisabled && (
                  <div className={styles.upload_btn_col}>
                    <S3UploadFile
                      onChange={(newFiles) => {
                        setFiles([...files, ...newFiles]);
                      }}
                      type='chat_icon'
                      name='filename'
                      multiple={true}
                      cta=''
                    />
                  </div>
                )}
                <div className={styles.message_input_col}>
                  <TextareaAutosize
                    className={styles.message_box}
                    value={currentMessage}
                    onChange={(e) => onBodyChange(e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && heldKey !== 'Shift') {
                        send(e);
                      } else if (e.key === 'Shift') {
                        setHeldKey(e.key);
                      }
                    }}
                    onKeyUp={(e) => {
                      if (e.key === heldKey) {
                        setHeldKey(null);
                      }
                    }}
                    onFocus={() => onRead()}
                    placeholder='Type a message...'
                    minRows={1}
                    maxRows={5}
                  />
                  <div className={styles.url_preview_wrap}>
                    {isUrlPreviewLoading && <LoadingScreen size={'20'} />}
                    {!isUrlPreviewLoading && urlMetadata && (
                      <UrlPreview metadata={urlMetadata} onDelete={removeUrlPreview} />
                    )}
                  </div>
                </div>
                <div className={styles.send_message_col}>
                  <SendMessageButton
                    className={styles.send_message_button}
                    disabledClassName={styles.disabled}
                    onClick={send}
                    disabled={currentMessage === '' && files.length === 0}
                  />
                </div>
              </div>
            )}
          </div>

          <RequestCallModal
            open={callModal === CALL_MODAL_REQUEST}
            otherUserFirstName={mentorFirstName}
            afterHoursRange={callHoursRange}
            callable={callable}
            conversationId={conversation?.uuid}
            incentive={callIncentive}
            onClose={closeCallModal}
            onCallCreated={handleCalloutCallCreated}
            onScheduleInstead={() => setCallModal(CALL_MODAL_SCHEDULE)}
          />

          <ScheduleCallModal
            open={callModal === CALL_MODAL_SCHEDULE}
            conversationId={conversation?.uuid}
            otherUserTimezone={otherUserTimezone}
            viewerTimezone={user?.timezone}
            otherUserFirstName={mentorFirstName}
            otherUserName={otherUserName}
            otherUserPhoto={otherUserImageUrl}
            incentive={callIncentive}
            onClose={closeCallModal}
            onCallCreated={handleCalloutCallCreated}
          />
        </div>
      )}
    </div>
  );
}
